import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search,
  Plus,
  Inbox,
  FileText,
  File,
  Image,
  ChevronRight,
  Sparkles,
  Loader2,
  CheckSquare,
  Square,
  AlertTriangle,
  Type,
  Eye,
  CheckCircle2
} from 'lucide-react';
import AddDocumentDialog from '@/components/inbox/AddDocumentDialog';
import BulkResultsDialog from '@/components/inbox/BulkResultsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPES = ['Quote', 'Invoice', 'Order Confirmation', 'Packing Slip', 'Other'];
const STATUS_OPTIONS = ['New', 'Reviewed', 'Matched to Order'];

const InboxPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadTab, setUploadTab] = useState('file');

  // Selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk parse
  const [bulkParsing, setBulkParsing] = useState(false);
  const [showBulkResults, setShowBulkResults] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  // Add document form
  const [newDoc, setNewDoc] = useState({
    title: '',
    sender: '',
    date_received: new Date().toISOString().split('T')[0],
    document_type: 'Other',
    text_content: '',
    project_id: '',
    order_id: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Poll for parse_status updates when any doc is pending/parsing
  useEffect(() => {
    const hasPending = documents.some(d => d.parse_status === 'pending' || d.parse_status === 'parsing');
    if (!hasPending) return;
    const interval = setInterval(() => {
      axios.get(`${API}/documents/`, { withCredentials: true })
        .then(res => setDocuments(res.data))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [documents]);

  const fetchData = async () => {
    try {
      const [docsRes, projRes] = await Promise.all([
        axios.get(`${API}/documents/`, { withCredentials: true }),
        axios.get(`${API}/projects/`, { withCredentials: true }),
      ]);
      setDocuments(docsRes.data);
      setProjects(projRes.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersForProject = async (projectId) => {
    if (!projectId) { setOrders([]); return; }
    try {
      const res = await axios.get(`${API}/orders/project/${projectId}`, { withCredentials: true });
      setOrders(res.data);
    } catch { setOrders([]); }
  };

  const handleAddDocument = async () => {
    if (uploadTab === 'file' && !selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    if (uploadTab === 'text' && !newDoc.text_content.trim()) {
      toast.error('Please paste some text content');
      return;
    }
    if (!newDoc.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setCreating(true);
    try {
      if (uploadTab === 'file' && selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', newDoc.title);
        formData.append('sender', newDoc.sender);
        formData.append('date_received', newDoc.date_received);
        formData.append('document_type', newDoc.document_type);
        formData.append('project_id', newDoc.project_id || '');
        formData.append('order_id', newDoc.order_id || '');

        await axios.post(`${API}/documents/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${API}/documents/`, {
          title: newDoc.title,
          sender: newDoc.sender,
          date_received: newDoc.date_received,
          document_type: newDoc.document_type,
          text_content: newDoc.text_content,
          project_id: newDoc.project_id || null,
          order_id: newDoc.order_id || null,
        }, { withCredentials: true });
      }

      toast.success('Document added!');
      setShowAddDoc(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewDoc({
      title: '', sender: '', date_received: new Date().toISOString().split('T')[0],
      document_type: 'Other', text_content: '', project_id: '', order_id: '',
    });
    setSelectedFile(null);
    setUploadTab('file');
    setOrders([]);
  };

  // === Selection handlers ===
  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedIds(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleDoc = (docId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const selectAll = () => {
    const parseable = filteredDocs.filter(d => d.text_content || d.file_path);
    if (selectedIds.size === parseable.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(parseable.map(d => d.document_id)));
    }
  };

  // === Bulk parse ===
  const handleBulkParse = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('No documents selected'); return; }

    setBulkParsing(true);
    setBulkResults(null);
    try {
      const res = await axios.post(`${API}/documents/bulk-parse`, {
        document_ids: ids,
      }, { withCredentials: true, timeout: 300000 });

      setBulkResults(res.data);
      setShowBulkResults(true);

      const { parsed, errors } = res.data.summary;
      if (errors === 0) {
        toast.success(`All ${parsed} documents parsed successfully!`);
      } else {
        toast.warning(`${parsed} parsed, ${errors} failed`);
      }

      // Exit select mode and refresh
      setSelectMode(false);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Bulk parsing failed';
      if (err.response?.status === 503) {
        toast.error('AI parsing is not configured. Contact your administrator.');
      } else {
        toast.error(detail);
      }
    } finally {
      setBulkParsing(false);
    }
  };

  const filteredDocs = useMemo(() => {
    let result = [...documents];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(d =>
        d.title?.toLowerCase().includes(q) ||
        d.sender?.toLowerCase().includes(q) ||
        d.project_name?.toLowerCase().includes(q)
      );
    }
    if (typeFilter !== 'all') result = result.filter(d => d.document_type === typeFilter);
    if (statusFilter !== 'all') result = result.filter(d => d.status === statusFilter);
    if (reviewFilter !== 'all') {
      result = result.filter(d => {
        const hasFields = d.parse_status === 'parsed' && d.extracted_data?.total_fields_count > 0;
        if (!hasFields) return false;
        return (d.review_status || 'unreviewed') === reviewFilter;
      });
    }
    return result;
  }, [documents, searchTerm, typeFilter, statusFilter, reviewFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Quote': return 'bg-blue-500 text-white';
      case 'Invoice': return 'bg-purple-500 text-white';
      case 'Order Confirmation': return 'bg-green-500 text-white';
      case 'Packing Slip': return 'bg-orange-500 text-zinc-900';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-primary text-zinc-900';
      case 'Reviewed': return 'bg-blue-500 text-white';
      case 'Matched to Order': return 'bg-green-500 text-white';
      default: return 'bg-zinc-100 text-zinc-900';
    }
  };

  const getDocIcon = (doc) => {
    if (doc.file_type?.includes('pdf')) return <FileText className="w-5 h-5" />;
    if (doc.file_type?.includes('image')) return <Image className="w-5 h-5" />;
    if (doc.text_content) return <Type className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const newCount = documents.filter(d => d.status === 'New').length;
  const parseableInFilter = filteredDocs.filter(d => d.text_content || d.file_path);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-24" data-testid="inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide tracking-tight mb-1">Procurement Inbox</h1>
          <p className="text-zinc-500 text-sm sm:text-base">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
            {newCount > 0 && <span className="ml-2 text-primary font-bold">{newCount} new</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSelectMode}
            data-testid="toggle-select-mode-btn"
            className={`inline-flex items-center gap-2 h-12 px-4 font-bold uppercase border text-sm transition-colors ${
              selectMode
                ? 'bg-zinc-900 text-white border-zinc-900'
                : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            {selectMode ? 'Cancel' : 'Bulk Parse'}
          </button>
          <button
            onClick={() => { resetForm(); setShowAddDoc(true); }}
            data-testid="add-document-btn"
            className="inline-flex items-center gap-2 h-12 px-5 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-primary-hover active:translate-y-1 transition-all text-sm"
          >
            <Plus className="w-5 h-5" />
            Add Document
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="doc-search-input"
            className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="doc-type-filter"
          className="h-14 border border-zinc-200 bg-white text-zinc-900 px-4 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Types</option>
          {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="doc-status-filter"
          className="h-14 border border-zinc-200 bg-white text-zinc-900 px-4 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={reviewFilter}
          onChange={(e) => setReviewFilter(e.target.value)}
          data-testid="doc-review-filter"
          className="h-14 border border-zinc-200 bg-white text-zinc-900 px-4 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Review</option>
          <option value="unreviewed">Needs Review</option>
          <option value="in_progress">In Progress</option>
          <option value="verified">Verified</option>
        </select>
      </div>

      {/* Select All bar */}
      {selectMode && filteredDocs.length > 0 && (
        <div className="flex items-center justify-between bg-zinc-900 text-white px-4 py-3 mb-3 border border-zinc-700" data-testid="select-all-bar">
          <button
            onClick={selectAll}
            data-testid="select-all-btn"
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide hover:text-primary transition-colors"
          >
            {selectedIds.size === parseableInFilter.length && parseableInFilter.length > 0 ? (
              <CheckSquare className="w-4 h-4 text-primary" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            {selectedIds.size === parseableInFilter.length && parseableInFilter.length > 0 ? 'Deselect All' : `Select All (${parseableInFilter.length})`}
          </button>
          <span className="text-xs font-mono text-zinc-400">{selectedIds.size} selected</span>
        </div>
      )}

      {/* Document List */}
      {loading ? (
        <div className="grid gap-3" data-testid="inbox-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white border border-zinc-200 p-4 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-zinc-100 border border-zinc-200" />
                <div className="flex-1">
                  <div className="h-5 w-48 bg-zinc-100 mb-2" />
                  <div className="h-3 w-32 bg-zinc-50" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 sm:p-12 text-center" data-testid="inbox-empty-state">
          <Inbox className="w-20 h-20 mx-auto mb-4 text-zinc-500" />
          <h2 className="text-xl sm:text-2xl font-heading font-bold uppercase tracking-wide mb-3">
            {documents.length === 0 ? 'No Documents Yet' : 'No Matching Documents'}
          </h2>
          <p className="text-zinc-500 max-w-md mx-auto leading-relaxed">
            {documents.length === 0
              ? 'Upload procurement documents to keep everything organized. Quotes, invoices, order confirmations — all in one place.'
              : 'Try adjusting your search or filters.'
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredDocs.map((doc) => {
            const hasParseable = !!(doc.text_content || doc.file_path);
            const isSelected = selectedIds.has(doc.document_id);

            return (
              <div
                key={doc.document_id}
                className={`w-full bg-white border p-4 text-left transition-all ${
                  selectMode && isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-zinc-200 hover:translate-x-1 hover:translate-y-1'
                }`}
                data-testid={`doc-card-${doc.document_id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox in select mode */}
                  {selectMode && (
                    <button
                      onClick={() => hasParseable && toggleDoc(doc.document_id)}
                      disabled={!hasParseable}
                      data-testid={`doc-select-${doc.document_id}`}
                      className={`flex-shrink-0 ${!hasParseable ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {isSelected ? (
                        <CheckSquare className="w-6 h-6 text-primary" />
                      ) : (
                        <Square className="w-6 h-6 text-zinc-400" />
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => selectMode ? (hasParseable && toggleDoc(doc.document_id)) : navigate(`/inbox/${doc.document_id}`)}
                    className="flex items-center gap-4 flex-1 min-w-0 text-left"
                  >
                    <div className={`w-10 h-10 border border-zinc-200 flex items-center justify-center flex-shrink-0 ${
                      doc.status === 'New' ? 'bg-primary' : 'bg-zinc-50'
                    }`}>
                      {getDocIcon(doc)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-sm sm:text-base truncate">{doc.title}</h3>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getTypeColor(doc.document_type)}`}>
                          {doc.document_type}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getStatusColor(doc.status)}`}>
                          {doc.status}
                        </span>
                        {doc.parse_status === 'parsing' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300 inline-flex items-center gap-1" data-testid={`parse-status-${doc.document_id}`}>
                            <Loader2 className="w-3 h-3 animate-spin" /> Parsing
                          </span>
                        )}
                        {doc.parse_status === 'parsed' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-green-100 text-green-700 border border-green-300 inline-flex items-center gap-1" data-testid={`parse-status-${doc.document_id}`}>
                            <Sparkles className="w-3 h-3" /> Parsed
                          </span>
                        )}
                        {doc.parse_status === 'pending' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-zinc-100 text-zinc-500 border border-zinc-300" data-testid={`parse-status-${doc.document_id}`}>
                            Queued
                          </span>
                        )}
                        {doc.parse_status === 'error' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-red-100 text-red-600 border border-red-300 inline-flex items-center gap-1" data-testid={`parse-status-${doc.document_id}`}>
                            <AlertTriangle className="w-3 h-3" /> Parse Error
                          </span>
                        )}
                        {doc.parse_status === 'parsed' && doc.extracted_data?.total_fields_count > 0 && (
                          doc.review_status === 'verified' ? (
                            <span className="px-2 py-0.5 text-xs font-bold uppercase bg-primary text-black border border-black inline-flex items-center gap-1" data-testid={`review-status-${doc.document_id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Verified
                            </span>
                          ) : doc.review_status === 'in_progress' ? (
                            <span className="px-2 py-0.5 text-xs font-bold uppercase bg-amber-100 text-amber-800 border border-amber-500 inline-flex items-center gap-1" data-testid={`review-status-${doc.document_id}`}>
                              <Eye className="w-3 h-3" /> Review {doc.extracted_data.reviewed_fields_count}/{doc.extracted_data.total_fields_count}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-bold uppercase bg-white text-zinc-600 border border-zinc-400 border-dashed inline-flex items-center gap-1" data-testid={`review-status-${doc.document_id}`}>
                              <Eye className="w-3 h-3" /> Needs Review
                            </span>
                          )
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        {doc.sender && <span>{doc.sender}</span>}
                        <span>{formatDate(doc.date_received)}</span>
                        {doc.project_name && (
                          <span className="font-bold text-zinc-900 bg-zinc-50 px-2 py-0.5 border border-black">
                            {doc.project_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {!selectMode && (
                    <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Bulk Parse Bar */}
      {selectMode && selectedIds.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t-2 border-primary z-40 px-4 sm:px-8 py-3"
          data-testid="bulk-parse-bar"
        >
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="text-white">
              <span className="font-heading font-bold text-lg">{selectedIds.size}</span>
              <span className="text-zinc-400 text-sm ml-2">document{selectedIds.size !== 1 ? 's' : ''} selected</span>
            </div>
            <button
              onClick={handleBulkParse}
              disabled={bulkParsing}
              data-testid="bulk-parse-btn"
              className="inline-flex items-center gap-2 h-12 px-6 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm border border-primary hover:bg-primary-hover disabled:opacity-60 transition-colors"
            >
              {bulkParsing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Parsing {selectedIds.size} docs...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Parse {selectedIds.size} Document{selectedIds.size !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Parsing Overlay */}
      {bulkParsing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="bulk-parsing-overlay">
          <div className="bg-white border border-zinc-200 p-8 text-center max-w-sm mx-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="font-heading font-bold text-xl uppercase tracking-wide mb-2">Parsing Documents</h2>
            <p className="text-sm text-zinc-500">
              Analyzing {selectedIds.size} document{selectedIds.size !== 1 ? 's' : ''} with AI...
            </p>
            <p className="text-xs text-zinc-400 mt-2">This may take a minute.</p>
          </div>
        </div>
      )}

      <BulkResultsDialog
        open={showBulkResults}
        onOpenChange={setShowBulkResults}
        bulkResults={bulkResults}
        documents={documents}
        onViewDoc={(docId) => { setShowBulkResults(false); navigate(`/inbox/${docId}`); }}
      />

      <AddDocumentDialog
        open={showAddDoc}
        onOpenChange={setShowAddDoc}
        uploadTab={uploadTab}
        setUploadTab={setUploadTab}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        newDoc={newDoc}
        setNewDoc={setNewDoc}
        projects={projects}
        orders={orders}
        creating={creating}
        onSave={handleAddDocument}
        onProjectChange={(pid) => {
          setNewDoc({ ...newDoc, project_id: pid, order_id: '' });
          fetchOrdersForProject(pid);
        }}
      />
    </div>
  );
};

export default InboxPage;
